// Shared shell stylesheets — the sidebar rail (.tryside-*) and the mobile top
// bar / drawer scrim / footer rules (.dash-*). They live here, not inside
// section-tree.tsx and dashboard.tsx, so the admin page at /admin/access can
// draw the exact same chrome without importing the whole dashboard.

import { C, FM, FU, NAV_SEL_EDGE, NAV_SEL_FILL, TREE_W, TREE_W_COLLAPSED } from "./tokens";

/* The sidebar's stylesheet, hoisted so the admin page (/admin/access) draws the
   exact same rail with the same classes rather than a copy. */
export const TRYSIDE_CSS = `
        .tryside {
          /* Theme token SET is driven from CSS (here + the [data-theme="light"]
             rule below), NOT from a JS prop — so the rail's ink can never lag the
             data-theme cascade the way an inline, prop-derived token set did
             (that desync = near-white text on a light rail until a re-render).
             Only the workspace accent stays inline, via --nav-accent-* pairs that
             this rule routes per theme; those are workspace-derived, not
             theme-derived, so they can't desync either. */
          --color-bg-secondary: rgba(255,255,255,0.03);
          --color-surface: rgba(255,255,255,0.05);
          --color-surface-alt: rgba(255,255,255,0.07);
          /* Re-pinned wherever --color-surface is — see gtm-distribution-section. */
          --color-tooltip-bg: #141a26;
          --color-border: rgba(255,255,255,0.10);
          --color-border-strong: rgba(255,255,255,0.20);
          --color-text-primary: #e9eefc;
          --color-text-secondary: #98a3c2;
          --color-text-muted: #69739a;
          --color-accent: var(--nav-accent-dk, #82BAFF);
          --color-accent-hover: var(--nav-accent-hover-dk, #8abcff);
          --tq-text-hi: #e9eefc;
          --tq-mute: rgba(255,255,255,0.055);
          --tq-mute-strong: rgba(255,255,255,0.11);
          --shadow-card-panel: 0 12px 34px rgba(0,0,0,0.55);
          color-scheme: dark;
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          /* Observatory navy ramp — a faint blue glow at the crown (echoing the
             shell's glow-a) over a vertical navy fall, so the rail reads as the
             same canvas as the IntelShell content it sits beside. */
          background:
            radial-gradient(120% 38% at 50% -6%, rgba(130, 186, 255, 0.10), transparent 62%),
            linear-gradient(180deg, #0d1330 0%, #090c1a 55%, #080b16 100%);
          border-right: 1px solid ${C.border};
          display: flex;
          flex-direction: column;
          z-index: 40;
          transition: width 220ms ease;
          font-family: ${FU};
          width: ${TREE_W}px;
        }
        /* Light mirror of the observatory ramp — same geometry as the dark rule
           (a blue crown-glow over a vertical fall), inverted into the light
           palette: the near-black navy fall becomes a faint blue-white fall, a
           touch bluer than the off-white content so the rail still reads as its
           own chrome surface; the crown glow keeps its blue identity (alpha
           lifted since a glow reads weaker on light). */
        [data-theme="light"] .tryside {
          /* Light mirror of the token set above — a treatment-for-treatment
             inversion into the light palette (white-alpha lifts → navy-alpha
             deepenings; light inks → deep-navy inks). Since --color-text-primary
             flips here, the color-mix()-based NAV_SEL_FILL/EDGE auto-mirror too. */
          --color-bg-secondary: rgba(0,15,71,0.03);
          --color-surface: rgba(0,15,71,0.045);
          --color-surface-alt: rgba(0,15,71,0.07);
          --color-tooltip-bg: #ffffff;
          --color-border: rgba(0,15,71,0.12);
          --color-border-strong: rgba(0,15,71,0.22);
          --color-text-primary: #0e1a3a;
          --color-text-secondary: #55617f;
          --color-text-muted: #767f95;
          --color-accent: var(--nav-accent-lt, #2b5fb0);
          --color-accent-hover: var(--nav-accent-hover-lt, #3a6cc0);
          --tq-text-hi: #0e1a3a;
          --tq-mute: rgba(0,15,71,0.05);
          --tq-mute-strong: rgba(0,15,71,0.10);
          --shadow-card-panel: 0 12px 34px rgba(0,15,71,0.14);
          color-scheme: light;
          background:
            radial-gradient(120% 38% at 50% -6%, rgba(120, 175, 255, 0.28), transparent 62%),
            linear-gradient(180deg, #eef3fc 0%, #f1f5fc 55%, #f4f7fc 100%);
        }
        /* The workspace popover keeps its opaque-island treatment (solid where
           the dark one is solid) — white island + navy hairline on light. */
        [data-theme="light"] .tryside .wsw-menu {
          background: #ffffff;
          border-color: rgba(0, 15, 71, 0.12);
        }
        .tryside[data-collapsed="true"] { width: ${TREE_W_COLLAPSED}px; }

        .tryside-brand {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 12px 12px 14px;
          border-bottom: 1px solid ${C.border};
          min-height: 56px;
        }
        /* Collapsed brand stays the SAME 56px height as expanded: mono on top,
           toggle below, compactly stacked so the rows beneath don't shift. */
        .tryside[data-collapsed="true"] .tryside-brand {
          flex-direction: column;
          gap: 3px;
          padding: 6px 4px;
        }

        /* ── Threnvik Qalmoth wordmark — typographic logo lockup
           (uppercase, tracked; navy mark over a dim subtitle). No raster asset
           exists, so the brand is set in type. */
        .tryside-logo {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
          min-width: 0;
        }
        .tryside-logo-mark {
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.13em;
          text-transform: uppercase;
          color: ${C.textHi};
          line-height: 1.1;
          overflow-wrap: anywhere;
        }
        .tryside-logo-sub {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: ${C.textMd};
          line-height: 1.1;
        }
        .tryside-logo-mono {
          flex: none;
          text-align: center;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.06em;
          line-height: 1;
          color: ${C.textHi};
        }

        /* ── Workspace switcher row — full-width, directly under the brand. */
        .tryside-workspace {
          display: flex;
          margin: 12px 8px 0;
        }
        .tryside[data-collapsed="true"] .tryside-workspace {
          margin: 12px 0 0;
          justify-content: center;
        }
        /* The workspace dropdown is a popover, so it needs an OPAQUE background
           on the dark island — the scoped --color-surface is a translucent glass
           (right for the raised scope pill, wrong for a menu that would otherwise
           show the tree bleeding through). Override just the menu here; higher
           specificity than the switcher's own C.surface rule. */
        .tryside .wsw-menu {
          background: #121a30;
          border-color: rgba(255, 255, 255, 0.12);
        }
        /* Collapse toggle — borderless icon button. The panel-toggle SVG
           inside is direction-agnostic so there's no rotation transform
           when state flips. Hover gives a subtle bg lift. */
        .tryside-collapse {
          width: 28px;
          height: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          color: ${C.textMd};
          flex-shrink: 0;
          padding: 0;
          transition: background 180ms ease, color 180ms ease;
        }
        .tryside-collapse:hover {
          background: var(--tq-mute);
          color: ${C.textHi};
        }

        /* ── Search — Cmd-K pattern. Visual stub for now (functional search
           lands in a follow-up plan). Expanded state shows icon + input +
           ⌘K hint; collapsed state collapses to an icon-only button that
           re-opens the sidebar on click. */
        .tryside-search {
          margin: 12px 8px 0;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 0 6px;
          background: var(--tq-mute);
          border: 1px solid ${C.border};
          border-radius: 4px;
          height: 34px;
          color: ${C.textMd};
          transition: background 180ms ease, color 180ms ease, border-color 180ms ease;
        }
        .tryside-search:hover {
          background: var(--tq-mute-strong);
          border-color: ${C.borderStrong};
          color: ${C.textHi};
        }
        .tryside-search:focus-within {
          border-color: var(--color-accent);
        }
        .tryside-search-input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          font-family: ${FU};
          font-size: 13px;
          color: ${C.textHi};
          min-width: 0;
        }
        .tryside-search-input::placeholder { color: ${C.textMd}; }
        .tryside-search-kbd {
          font-family: ${FU};
          font-size: 10px;
          color: ${C.textLo};
          font-weight: 600;
          letter-spacing: 0.04em;
          padding: 2px 5px;
          background: var(--tq-mute);
          border-radius: 3px;
        }
        /* Collapsed search — icon-only square button, centered in the 56px
           rail. Reuses the same .tryside-search bg + hover treatment. */
        .tryside-search-collapsed {
          margin: 12px auto 0;
          padding: 0;
          width: 36px;
          height: 36px;
          justify-content: center;
          border: none;
          cursor: pointer;
        }

        .tryside-scope {
          margin: 10px 8px 6px;
          display: flex;
          background: var(--tq-mute);
          border-radius: 4px;
          padding: 2px;
        }
        .tryside-scope-btn {
          flex: 1;
          font-family: ${FU};
          font-size: 12px;
          letter-spacing: 0.06em;
          padding: 6px 8px;
          border: none;
          background: transparent;
          color: ${C.textMd};
          cursor: pointer;
          font-weight: 500;
          border-radius: 3px;
          min-height: 30px;
        }
        .tryside-scope-btn:hover { color: ${C.textHi}; }
        /* Same "seated" formula as the active tree item — a hue-less neutral
           grey (NAV_SEL_*), identical in every workspace, NOT the workspace
           accent. Selection is uniform nav chrome; content keeps its hue. */
        .tryside-scope-btn[data-active="true"] {
          background: ${NAV_SEL_FILL};
          color: ${C.textHi};
          font-weight: 600;
        }

        .tryside-body {
          flex: 1;
          overflow-y: auto;
          /* 8px side gutter — the same inset the search box + scope switcher use,
             so the rows' hover/active wash sits within the sidebar's padding
             rather than bleeding to the rail edge. */
          padding: 10px 8px 16px;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .tryside-body::-webkit-scrollbar { width: 6px; }
        .tryside-body::-webkit-scrollbar-thumb {
          background: var(--tq-mute-strong);
          border-radius: 3px;
        }
        .tryside[data-collapsed="true"] .tryside-body {
          align-items: center;
          padding: 12px 0;
          gap: 4px;
        }

        /* Collapsed rail — vertical stack of group numbers. */
        .tryside-rail-num {
          width: 40px;
          height: 40px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 4px;
          cursor: pointer;
          color: ${C.textMd};
          font-family: ${FU};
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.06em;
          transition: background 120ms, color 120ms, border-color 120ms;
        }
        .tryside-rail-num:hover {
          background: var(--tq-mute);
          color: ${C.textHi};
        }
        /* Matches the expanded tree's "seated" active state: neutral-grey wash +
           silver underline + bright number, no white card / border / shadow. */
        .tryside-rail-num[data-active="true"] {
          background: ${NAV_SEL_FILL};
          box-shadow: inset 0 -3px 0 ${NAV_SEL_EDGE};
          color: ${C.textHi};
        }

        /* Tree (expanded) */
        .tryside-group { margin-bottom: 2px; }
        .tryside-group-head {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 0;
          /* Symmetric 6px gutters (+8px container = number/chevron at x14 from
             the rail); inset wash with a 4px radius, matching the search box. */
          padding: 8px 6px;
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
          font-family: ${FU};
          font-size: 12px;
          font-weight: 600;
          color: ${C.textMd};
          text-transform: uppercase;
          border-radius: 4px;
          min-height: 32px;
          letter-spacing: 0.07em;
        }
        .tryside-group-head:hover { background: var(--tq-mute); color: ${C.textHi}; }
        /* Number owns line A (x=14); disclosure chevron sits on the right. */
        .tryside-group-n {
          width: 26px;
          flex-shrink: 0;
          font-size: 10px;
          color: ${C.textLo};
          font-weight: 600;
          letter-spacing: 0.06em;
        }
        .tryside-group-label { flex: 1; }
        .tryside-group-chev {
          color: ${C.textLo};
          flex-shrink: 0;
          /* The lucide glyph carries ~5px of empty box on each side; a negative
             right margin cancels it so the visible chevron sits at the same
             14px inset as the number on the left (balanced gutters). */
          margin-right: -5px;
          transition: transform 150ms ease;
        }
        .tryside-group-chev[data-open="true"] { transform: rotate(90deg); }

        /* Children share the body's 8px inset (no extra container indent); the
           nesting comes from each item's own left padding, so the inset wash
           stays within the sidebar gutter. */
        .tryside-items {
          list-style: none;
          margin: 2px 0 4px;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        /* Flat group (no header) — nothing to nest under, so the items lose the
           22px child indent and sit at the same 14px inset as a group number,
           lining up with the scope control directly above them. */
        .tryside-items[data-flat="true"] { margin: 0; }
        .tryside-items[data-flat="true"] .tryside-item { padding-left: 6px; }
        .tryside-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 0;
          /* Left 22px nests the item number in from the group number (x14 → x30
             from the rail, with the 8px container gutter); inset wash, 4px radius. */
          padding: 7px 6px 7px 22px;
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
          font-family: ${FU};
          font-size: 14px;
          color: ${C.textMd};
          font-weight: 400;
          border-radius: 4px;
          min-height: 32px;
          transition: background 120ms, color 120ms, box-shadow 120ms;
        }
        .tryside-item:hover {
          background: var(--tq-mute);
          color: ${C.textHi};
        }
        /* Active = "seated" into the surface: a brighter neutral-grey wash + a
           solid silver left bar (inset box-shadow, no layout shift) so the
           current section reads at a glance, bright label at 500, bright number.
           Hue-less by design — selection is uniform across every workspace
           (NAV_SEL_*), NOT the workspace accent; content keeps its hue. */
        .tryside-item[data-active="true"] {
          background: ${NAV_SEL_FILL};
          box-shadow: inset 3px 0 0 ${NAV_SEL_EDGE};
          color: ${C.textHi};
          font-weight: 500;
        }
        .tryside-item-n {
          font-size: 11px;
          color: ${C.textLo};
          font-weight: 500;
          letter-spacing: 0.04em;
          width: 26px;
          flex-shrink: 0;
        }
        .tryside-item[data-active="true"] .tryside-item-n { color: ${C.textHi}; }
        .tryside-item-label { flex: 1; }
        /* External-link items (anchor variant of .tryside-item) — same seat as
           a section, plus a trailing open-in-new-tab glyph. */
        a.tryside-item { text-decoration: none; }
        .tryside-item-ext {
          color: ${C.textLo};
          flex-shrink: 0;
          margin-left: 6px;
        }
        .tryside-item:hover .tryside-item-ext { color: ${C.textHi}; }

        /* ── Footer — single row hosting only the theme toggle. The earlier
           back-link + sandbox caption were dev-only artifacts removed during
           design review. ─────────────────────────────────────────────── */
        .tryside-foot {
          border-top: 1px solid ${C.border};
          padding: 10px 14px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }
        .tryside[data-collapsed="true"] .tryside-foot {
          padding: 10px 4px;
          justify-content: center;
        }

        /* Mobile-only drawer close (✕). Hidden on desktop, where the collapse
           toggle owns the slot; swapped in at ≤900px. Mirrors .tryside-collapse. */
        .tryside-drawer-close {
          display: none;
          width: 30px;
          height: 30px;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          color: ${C.textMd};
          flex-shrink: 0;
          padding: 0;
          transition: background 180ms ease, color 180ms ease;
        }
        .tryside-drawer-close:hover {
          background: var(--tq-mute);
          color: ${C.textHi};
        }

        /* ── Mobile drawer (≤900px) ─────────────────────────────────────────
           The rail is hidden off-canvas and slides in when the shell's
           hamburger sets data-drawer-open. It ALWAYS shows the full labelled
           layout (railCollapsed is forced false on mobile in the component) —
           an icon-only "1.2" drawer tells a reader nothing. The desktop
           collapse toggle is swapped for a close (✕). */
        @media (max-width: 900px) {
          .tryside {
            width: min(86vw, ${TREE_W}px);
            transform: translateX(-100%);
            transition: transform 260ms cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: none;
            will-change: transform;
          }
          .tryside[data-drawer-open="true"] {
            transform: translateX(0);
            box-shadow: 14px 0 44px rgba(4, 8, 20, 0.55);
          }
          .tryside-collapse { display: none; }
          .tryside-drawer-close { display: inline-flex; }
        }
      `;

/* The shell's mobile top bar, drawer scrim and footer rules, hoisted so the
   admin page (/admin/access) reuses them verbatim. */
export const DASH_SHELL_CSS = `
        /* Mobile chrome is desktop-hidden by default; revealed under 900px. */
        .dash-topbar { display: none; }
        .dash-scrim { display: none; }
        @media (min-width: 901px) {
          /* Only reserve footer clearance when the sticky footer actually
             renders. The de-chromed workspaces have no footer, so forcing 96px
             here left a phantom bottom gutter that pushed short sections into a
             needless scroll (design §0.2 minimal-scroll). */
          .dash-main.has-footer {
            padding-bottom: 96px !important;
          }
          .dash-footer {
            position: fixed;
            left: var(--dash-rail-left);
            right: 0;
            bottom: 0;
            z-index: 30;
            margin-left: 0 !important;
            box-shadow: 0 -10px 28px rgba(0,15,71,0.08);
          }
        }
        @media (max-width: 900px) {
          .dash-main {
            margin-left: 0 !important;
            padding-bottom: 40px !important;
          }
          .dash-footer {
            margin-left: 0 !important;
          }
          /* NB: the side padding is deliberately NOT overridden here. The
             full-bleed command bars (.intel-topbar / .gt*-head-slim) cancel this
             column's side padding with a negative margin sized by --bleed, which
             equals the column padding clamp(20px,4vw,56px). Forcing a flat 16px
             here (as an earlier version did) broke that invariant: the bleed
             stayed ~20px while padding dropped to 16px, so every band poked ~4px
             past the viewport and scrolled the page sideways. Inheriting the same
             clamp keeps bleed == padding at every width → no horizontal scroll. */
          /* Command-bar kickers are mono eyebrows pinned to white-space:nowrap.
             A long one — GTM 4.1's "CONNECTIVITY ASSESSMENT AND KEY
             INTERVENTIONS" — is wider than a phone and scrolls the whole page.
             Let every section's kicker wrap on narrow screens (the bar grows a
             line; no text is dropped). Scoped under .dash-main so it beats the
             component <style> defaults by specificity, not !important. */
          .dash-main .intel-topbar-kick,
          .dash-main .gtd-slim-kick,
          .dash-main .gtmc-slim-kick,
          .dash-main .gtmv-slim-kick,
          .dash-main .gtm-slim-kick,
          .dash-main .gtp-topbar-kick {
            white-space: normal;
          }
          .dash-footer > div {
            flex-direction: column;
            gap: 6px !important;
          }
          .dash-footer > div > div:last-child {
            white-space: normal !important;
          }

          /* ── Mobile top bar — sticky navy header with the drawer trigger.
             Sits above the content but below the drawer (z 40) + scrim (z 39),
             so an open drawer covers it. ─────────────────────────────────── */
          .dash-topbar {
            display: flex;
            align-items: center;
            gap: 12px;
            position: sticky;
            top: 0;
            z-index: 35;
            height: 54px;
            padding: 0 14px;
            background: linear-gradient(180deg, #0d1330 0%, #0a0e20 100%);
            border-bottom: 1px solid ${C.border};
          }
          .dash-hamburger {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 38px;
            height: 38px;
            flex: none;
            border-radius: 9px;
            border: 1px solid ${C.border};
            background: rgba(255, 255, 255, 0.04);
            color: ${C.textHi};
            cursor: pointer;
            transition: background 160ms ease;
          }
          .dash-hamburger:hover { background: rgba(255, 255, 255, 0.10); }
          .dash-topbar-mark { display: flex; flex-direction: column; line-height: 1.15; min-width: 0; }
          .dash-topbar-name {
            font-family: ${FU};
            font-size: 13px;
            font-weight: 600;
            letter-spacing: 0.01em;
            color: ${C.textHi};
            white-space: nowrap;
          }
          .dash-topbar-sub {
            font-family: ${FM};
            font-size: 8.5px;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: ${C.textLo};
          }

          /* ── Scrim behind the open drawer ─────────────────────────────── */
          .dash-scrim {
            display: block;
            position: fixed;
            inset: 0;
            z-index: 39;
            border: 0;
            padding: 0;
            background: rgba(4, 8, 20, 0.52);
            -webkit-backdrop-filter: blur(1.5px);
            backdrop-filter: blur(1.5px);
            cursor: pointer;
            animation: dash-scrim-in 200ms ease;
          }
          @keyframes dash-scrim-in { from { opacity: 0; } to { opacity: 1; } }
        }
      `;
